import React, { useMemo, useState } from 'react';
import { ChevronRight, Edit2, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

// Sector > Department > Team as one expandable table. Lead and Linked role come from
// the Org Chart roles linked to each team, so they stay in step with the chart.
const TeamsTreeTab = ({ tree, positions, users, canManageTeams, canManageSectors, onAdd, onEdit, onDeleteTeam }) => {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const needle = query.trim().toLowerCase();

  const rolesByTeam = useMemo(() => positions.reduce((map, position) => {
    if (position.team_id) (map[position.team_id] = map[position.team_id] || []).push(position);
    return map;
  }, {}), [positions]);
  const memberCount = useMemo(() => users.reduce((map, candidate) => {
    const ids = candidate.team_ids?.length ? candidate.team_ids : (candidate.team_id ? [candidate.team_id] : []);
    ids.forEach((id) => { map[id] = (map[id] || 0) + 1; });
    return map;
  }, {}), [users]);

  const sectors = useMemo(() => (tree.tree || []).map((sector) => {
    const departments = (sector.departments || []).map((department) => {
      const teams = (department.teams || []).filter((team) => !needle || team.name.toLowerCase().includes(needle) || department.name.toLowerCase().includes(needle) || sector.name.toLowerCase().includes(needle));
      return { ...department, teams };
    }).filter((department) => !needle || department.teams.length || department.name.toLowerCase().includes(needle));
    return { ...sector, departments };
  }).filter((sector) => !needle || sector.departments.length || sector.name.toLowerCase().includes(needle)), [tree, needle]);

  const toggle = (id) => setCollapsed((current) => ({ ...current, [id]: !current[id] }));
  const open = (id) => needle || !collapsed[id];

  const teamLead = (teamId) => {
    const assigned = (rolesByTeam[teamId] || []).filter((position) => position.user?.name)
      .sort((left, right) => (left.level || 0) - (right.level || 0));
    return assigned[0]?.user?.name;
  };
  const linkedRoles = (teamId) => {
    const roles = [...(rolesByTeam[teamId] || [])].sort((left, right) => (left.level || 0) - (right.level || 0));
    if (!roles.length) return <span className="text-slate-400">None</span>;
    return <span title={roles.map((role) => role.title).join(', ')}>{roles[0].title}{roles.length > 1 && <span className="text-slate-500"> +{roles.length - 1}</span>}</span>;
  };

  const Toggle = ({ id, label }) => <button type="button" onClick={() => toggle(id)} aria-expanded={open(id)} aria-label={`${open(id) ? 'Collapse' : 'Expand'} ${label}`} className="grid h-6 w-6 place-items-center rounded text-slate-500 hover:bg-slate-100">
    <ChevronRight size={14} className={`transition-transform ${open(id) ? 'rotate-90' : ''}`} />
  </button>;
  const RowActions = ({ type, item }) => canManageTeams && (type !== 'sector' || canManageSectors) ? <div className="flex justify-end gap-2">
    <button type="button" className="text-slate-400 hover:text-blue-600" onClick={() => onEdit(type, item)} aria-label={`Edit ${item.name}`}><Edit2 size={14} /></button>
    {type === 'team' && <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => onDeleteTeam(item)} aria-label={`Delete ${item.name}`}><Trash2 size={14} /></button>}
  </div> : null;

  const hasAny = sectors.length > 0;
  return <div data-testid="teams-tree-tab">
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
      <label className="flex h-10 min-w-[220px] max-w-xs flex-1 items-center gap-2 rounded-md border border-slate-200 bg-white px-3">
        <Search size={15} className="text-slate-400" aria-hidden="true" /><span className="sr-only">Search teams</span>
        <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search sectors, departments, teams" className="h-9 border-0 px-0 shadow-none focus-visible:ring-0" />
      </label>
      {canManageTeams && <DropdownMenu>
        <DropdownMenuTrigger asChild><Button className="ml-auto h-10 bg-blue-600 hover:bg-blue-700" data-testid="team-add-menu"><Plus size={15} className="mr-1.5" />Add</Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canManageSectors && <DropdownMenuItem onSelect={() => onAdd('sector')}>Sector</DropdownMenuItem>}
          <DropdownMenuItem onSelect={() => onAdd('department')}>Department</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onAdd('team')}>Team</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead className="bg-slate-50 text-xs text-slate-500"><tr>
          <th className="px-4 py-3 font-bold">Name</th><th className="px-4 py-3 font-bold">Lead</th><th className="px-4 py-3 font-bold">Linked role</th><th className="px-4 py-3 font-bold">Members</th><th className="px-4 py-3"><span className="sr-only">Actions</span></th>
        </tr></thead>
        <tbody>
          {sectors.map((sector) => <React.Fragment key={sector.id}>
            <tr className="h-11 border-t border-slate-100 bg-slate-50/60">
              <td className="px-4"><div className="flex items-center gap-1.5"><Toggle id={sector.id} label={sector.name} /><span className="font-bold text-slate-800">{sector.name}</span><span className="text-xs text-slate-500">Sector</span></div></td>
              <td /><td /><td className="px-4 text-slate-500">{sector.departments.length} dept{sector.departments.length === 1 ? '' : 's'}</td>
              <td className="px-4"><RowActions type="sector" item={sector} /></td>
            </tr>
            {open(sector.id) && sector.departments.map((department) => <React.Fragment key={department.id}>
              <tr className="h-11 border-t border-slate-100">
                <td className="px-4"><div className="flex items-center gap-1.5 pl-6"><Toggle id={department.id} label={department.name} /><span className="font-semibold text-slate-800">{department.name}</span><span className="text-xs text-slate-500">Department</span></div></td>
                <td /><td /><td className="px-4 text-slate-500">{department.teams.length} team{department.teams.length === 1 ? '' : 's'}</td>
                <td className="px-4"><RowActions type="department" item={department} /></td>
              </tr>
              {open(department.id) && department.teams.map((team) => <tr key={team.id} className="h-11 border-t border-slate-100" data-testid={`team-row-${team.id}`}>
                <td className="px-4"><span className="block pl-[4.25rem] text-slate-800">{team.name}</span></td>
                <td className="px-4 text-slate-700">{teamLead(team.id) || <span className="text-slate-400">No lead</span>}</td>
                <td className="px-4 text-slate-700">{linkedRoles(team.id)}</td>
                <td className="px-4 text-slate-600">{memberCount[team.id] || 0}</td>
                <td className="px-4"><RowActions type="team" item={team} /></td>
              </tr>)}
              {open(department.id) && !department.teams.length && <tr className="border-t border-slate-100"><td colSpan="5" className="py-2 pl-[5.25rem] text-xs text-slate-500">No teams in this department yet.</td></tr>}
            </React.Fragment>)}
          </React.Fragment>)}
          {!hasAny && <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-500">{needle ? 'Nothing matches this search.' : 'No sectors yet. Use Add to create the first one.'}</td></tr>}
        </tbody>
      </table>
    </div>
  </div>;
};

export default TeamsTreeTab;
